/**
 * PWAInstallPrompt — captures the browser's `beforeinstallprompt` event and
 * shows a friendly "Install on Home Screen?" modal after first successful
 * login. iOS Safari has no install API → we render manual instructions.
 *
 * Behaviour (iter196 — refined to be less naggy):
 *   - Skipped entirely when running standalone (already installed)
 *   - Tracked via localStorage `pwa_install_dismissed_at`; re-shows after 14 days
 *   - Capped at MAX_SHOWS_PER_USER (3) per device — after that, only via manual trigger
 *   - Logs `pwa_install_prompt_shown/dismissed/installed` analytics events
 *   - iOS branch logs `pwa_install_ios_dismissed` (no programmatic accept)
 *   - Manual trigger from Profile page via window.dispatchEvent('open-install-prompt')
 */
import { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Download, Share2, X, Smartphone, Sparkles } from 'lucide-react';
import { Button } from './ui/button';
import { useAuth } from '../context/AuthContext';
import { useBranding, brandingAssetUrl } from '../context/BrandingContext';
import {
  trackPwaPromptShown,
  trackPwaPromptDismissed,
  trackPwaInstalled,
  trackPwaIosDismissed,
} from '../lib/analytics';
import { claimPrompt } from '../lib/promptQueue';

const FOURTEEN_DAYS_MS = 14 * 24 * 60 * 60 * 1000;
const MAX_SHOWS_PER_USER = 3;
const STORAGE_KEY_BASE = 'pwa_install_dismissed_at';
const SHOW_COUNT_KEY_BASE = 'pwa_install_show_count';
const storageKey = (uid) => (uid ? `${STORAGE_KEY_BASE}:${uid}` : STORAGE_KEY_BASE);
const showCountKey = (uid) => (uid ? `${SHOW_COUNT_KEY_BASE}:${uid}` : SHOW_COUNT_KEY_BASE);

function isStandalone() {
  return (
    window.matchMedia?.('(display-mode: standalone)').matches ||
    window.navigator.standalone === true
  );
}

function isIOS() {
  return /iPad|iPhone|iPod/.test(window.navigator.userAgent) && !window.MSStream;
}

function getShowCount(uid) {
  try {
    return parseInt(localStorage.getItem(showCountKey(uid)) || '0', 10) || 0;
  } catch {
    return 0;
  }
}

function bumpShowCount(uid) {
  try { localStorage.setItem(showCountKey(uid), String(getShowCount(uid) + 1)); } catch { /* ignore */ }
}

function shouldShowAuto(uid) {
  if (isStandalone()) return false;
  if (getShowCount(uid) >= MAX_SHOWS_PER_USER) return false;
  const dismissedAt = parseInt(localStorage.getItem(storageKey(uid)) || '0', 10);
  if (Number.isFinite(dismissedAt) && Date.now() - dismissedAt < FOURTEEN_DAYS_MS) return false;
  // iter247 — the bare (un-suffixed) key acts as a global suppressor too,
  // so automated tests can silence the prompt before login (before uid is known).
  const globalDismissed = parseInt(localStorage.getItem(STORAGE_KEY_BASE) || '0', 10);
  if (Number.isFinite(globalDismissed) && Date.now() - globalDismissed < FOURTEEN_DAYS_MS) return false;
  return true;
}

export default function PWAInstallPrompt() {
  const { user } = useAuth();
  const { branding } = useBranding();
  const [deferred, setDeferred] = useState(null);
  const [open, setOpen] = useState(false);
  const shownLoggedRef = useRef(false);

  // Capture the install prompt the moment Chrome offers it
  useEffect(() => {
    const handler = (e) => {
      e.preventDefault();
      setDeferred(e);
    };
    window.addEventListener('beforeinstallprompt', handler);
    // Listen for the global `appinstalled` event — fires when install completes
    // even if dismissed elsewhere. Log it so adoption metrics stay accurate.
    const installedHandler = () => {
      trackPwaInstalled();
    };
    window.addEventListener('appinstalled', installedHandler);
    return () => {
      window.removeEventListener('beforeinstallprompt', handler);
      window.removeEventListener('appinstalled', installedHandler);
    };
  }, []);

  // Auto-show after first login (or when re-eligible)
  useEffect(() => {
    if (!user) return;
    if (user.simple_dashboard === undefined || user.simple_dashboard === null) return;
    if (!shouldShowAuto(user.id)) return;
    const t = setTimeout(() => {
      if (!claimPrompt('pwa-install')) return;
      setOpen(true);
    }, 1500);
    return () => clearTimeout(t);
  }, [user]);

  // Manual trigger event (Profile page button)
  useEffect(() => {
    const onManual = () => setOpen(true);
    window.addEventListener('open-install-prompt', onManual);
    return () => window.removeEventListener('open-install-prompt', onManual);
  }, []);

  // Track 'shown' once per open (and bump the show-count cap).
  useEffect(() => {
    if (open && !shownLoggedRef.current) {
      shownLoggedRef.current = true;
      trackPwaPromptShown();
      if (user?.id) bumpShowCount(user.id);
    }
    if (!open) shownLoggedRef.current = false;
  }, [open, user?.id]);

  if (!user || isStandalone()) return null;

  const dismiss = (markDismissed = true) => {
    if (markDismissed) {
      localStorage.setItem(storageKey(user?.id), String(Date.now()));
      // Track dismissal — distinct from install completion.
      if (isIOS()) trackPwaIosDismissed();
      else trackPwaPromptDismissed();
    }
    setOpen(false);
  };

  const install = async () => {
    if (deferred) {
      deferred.prompt();
      const { outcome } = await deferred.userChoice;
      setDeferred(null);
      if (outcome === 'accepted') {
        // Long-suppress; appinstalled handler will fire trackPwaInstalled too.
        localStorage.setItem(storageKey(user?.id), String(Date.now() + 365 * 24 * 60 * 60 * 1000));
      } else {
        // User clicked the native "Install" but then cancelled in the browser dialog.
        trackPwaPromptDismissed();
        dismiss(true);
      }
      setOpen(false);
    }
  };

  const ios = isIOS();

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center bg-black/40 backdrop-blur-sm p-3"
          onClick={() => dismiss(true)}
          data-testid="pwa-install-overlay"
        >
          <motion.div
            initial={{ y: 60, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 60, opacity: 0 }}
            transition={{ type: 'spring', stiffness: 280, damping: 26 }}
            className="bg-card border-2 border-border rounded-2xl shadow-2xl w-full max-w-md p-5 relative overflow-hidden"
            onClick={(e) => e.stopPropagation()}
            data-testid="pwa-install-modal"
          >
            <button
              onClick={() => dismiss(true)}
              className="absolute top-3 right-3 p-1 rounded-full hover:bg-muted press-scale"
              data-testid="pwa-install-close"
              aria-label="Close"
            >
              <X className="w-4 h-4" />
            </button>

            <div className="flex items-start gap-4 mb-4">
              <div className="relative shrink-0">
                <div className="w-16 h-16 rounded-2xl bg-primary/10 border-2 border-primary/30 flex items-center justify-center overflow-hidden">
                  <img
                    src={brandingAssetUrl(branding.logo_url)}
                    alt={branding.app_name}
                    className="w-12 h-12 object-contain"
                    onError={(e) => { e.currentTarget.src = '/icon-192.png'; }}
                  />
                </div>
                <Sparkles className="absolute -top-1 -right-1 w-5 h-5 text-accent fill-accent" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-display text-xl font-bold tracking-tight">
                  Add {branding.app_name} to your home screen
                </h3>
                <p className="text-sm text-muted-foreground mt-1">
                  Open it like a real app — faster, full-screen, works offline.
                </p>
              </div>
            </div>

            {ios ? (
              <div className="space-y-3 my-3">
                <p className="text-sm font-semibold flex items-center gap-2">
                  <Smartphone className="w-4 h-4 text-primary" />
                  On iPhone / iPad
                </p>
                <ol className="text-sm space-y-2 text-muted-foreground pl-1">
                  <li className="flex gap-2">
                    <span className="font-bold text-primary">1.</span>
                    <span>Tap the <Share2 className="inline w-3.5 h-3.5 mx-0.5 text-primary" /> Share button at the bottom of Safari.</span>
                  </li>
                  <li className="flex gap-2">
                    <span className="font-bold text-primary">2.</span>
                    <span>Scroll and tap <strong>Add to Home Screen</strong>.</span>
                  </li>
                  <li className="flex gap-2">
                    <span className="font-bold text-primary">3.</span>
                    <span>Tap <strong>Add</strong> in the top-right.</span>
                  </li>
                </ol>
              </div>
            ) : (
              <ul className="text-xs text-muted-foreground my-3 space-y-1">
                <li>• Quick launch from your home screen</li>
                <li>• Full-screen — no browser bars</li>
                <li>• Receives push notifications</li>
                <li>• Works during patchy internet</li>
              </ul>
            )}

            <div className="flex gap-2 mt-4">
              <Button
                variant="outline"
                onClick={() => dismiss(true)}
                className="flex-1 rounded-full press-scale"
                data-testid="pwa-install-later"
              >
                Maybe later
              </Button>
              {!ios && deferred && (
                <Button
                  onClick={install}
                  className="flex-1 rounded-full press-scale"
                  data-testid="pwa-install-now"
                >
                  <Download className="w-4 h-4 mr-1.5" />
                  Install
                </Button>
              )}
              {!ios && !deferred && (
                <Button
                  variant="outline"
                  onClick={() => dismiss(true)}
                  className="flex-1 rounded-full text-xs"
                  data-testid="pwa-install-instructions"
                >
                  Use your browser's "Install" menu
                </Button>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
